import { getLocalParticipant } from '../base/participants/functions';
import { SGI_ALLOWED_ORIGINS, SGI_DEBUG, SGI_URI_BR_URL } from './constants';
/**
 * Get the current SGI session.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {ISGISession|null}
 */
export function getSgiSession(state) {
    return state['features/sgi']?.session ?? null;
}
/**
 * Get the current SGI shared state.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {ISGISharedState|null}
 */
export function getSgiSharedState(state) {
    return state['features/sgi']?.sharedState ?? null;
}
/**
 * Check if there's an active SGI session.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isSgiSessionActive(state) {
    return state['features/sgi']?.session?.active ?? false;
}
/**
 * Check if the local participant is the SGI moderator (session owner).
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isLocalParticipantSGIModerator(state) {
    const session = getSgiSession(state);
    const localParticipant = getLocalParticipant(state);
    return Boolean(session?.moderatorId && session.moderatorId === localParticipant?.id);
}
/**
 * Check if the SGI iframe is ready.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {boolean}
 */
export function isSgiIframeReady(state) {
    return state['features/sgi']?.iframeReady ?? false;
}
/**
 * Build the SGI iframe URL with authentication and parameters.
 *
 * @param {boolean} isModerator - Whether the current user is the session moderator.
 * @param {Object} credentials - Optional credentials override.
 * @returns {string} The complete iframe URL.
 */
export function buildSGIIframeUrl(isModerator, credentials) {
    const baseUrl = SGI_URI_BR_URL || 'https://uri-br.confereweb.org.br/login';
    const url = new URL(baseUrl);
    // If base URL is just the root, append /login to ensure we hit the login page
    // and attempt to bypass any persistent session cookies by forcing a fresh login flow
    if (url.pathname === '/' || url.pathname === '') {
        url.pathname = '/login';
    }
    // Get credentials from URL params if not provided
    const urlParams = new URL(window.location.href).searchParams;
    const login = credentials?.login || urlParams.get('user') || '';
    let pass = credentials?.pass || urlParams.get('pass') || '';
    // Sanitize password (remove trailing slash if present)
    if (pass?.endsWith('/')) {
        pass = pass.slice(0, -1);
    }
    const watermark = credentials?.watermark || urlParams.get('watermark') || '';
    const relacaoCarga = credentials?.relacaoCarga || urlParams.get('relacao_carga') || '';
    const idAgendamento = credentials?.idAgendamento || urlParams.get('id_Agendamento') || '';
    const protocoloAno = credentials?.protocoloAno || urlParams.get('protocolo_ano') || '';
    // Set URL parameters
    url.searchParams.set('login', login);
    url.searchParams.set('pass', pass);
    url.searchParams.set('isModerator', String(isModerator));
    if (watermark) {
        url.searchParams.set('fiscal', watermark);
    }
    if (idAgendamento) {
        url.searchParams.set('id_Agendamento', idAgendamento);
    }
    if (protocoloAno) {
        url.searchParams.set('protocolo_ano', protocoloAno);
    }
    // Handle multiple relacao_carga values
    if (relacaoCarga) {
        const cargas = relacaoCarga.split(',');
        cargas.forEach((carga, index) => {
            if (carga.trim()) {
                url.searchParams.append(`relacao_carga_${index + 1}`, carga.trim());
            }
        });
    }
    // Debug log
    if (SGI_DEBUG) {
        console.log('[SGI] buildSGIIframeUrl params:', {
            login,
            pass: '***',
            watermark,
            relacaoCarga,
            idAgendamento,
            protocoloAno,
            isModerator,
            finalUrl: url.toString()
        });
    }
    // Add cache-busting timestamp to prevent browser caching with same credentials
    url.searchParams.set('_t', String(Date.now()));
    return url.toString();
}
/**
 * Validate postMessage origin.
 *
 * @param {string} origin - The origin of the received message.
 * @returns {boolean}
 */
export function isAllowedOrigin(origin) {
    return SGI_ALLOWED_ORIGINS.includes(origin);
}
/**
 * Retrieves a cookie value by name.
 *
 * @param {string} name - The name of the cookie.
 * @returns {string|undefined} The cookie value or undefined.
 */
export function getSgiCookie(name) {
    if (typeof document === 'undefined') {
        return undefined;
    }
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? match[2] : undefined;
}
/**
 * Get credentials from various sources with priority:
 * 1. URL params (highest priority)
 * 2. Local participant properties (set by middleware.any.ts from JWT/config)
 * 3. Config
 * 4. Cookies.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {Object}
 */
export function getSGICredentials(state) {
    const config = state['features/base/config'];
    const localParticipant = getLocalParticipant(state);
    // URL params (highest priority)
    const urlParams = new URL(window.location.href).searchParams;
    const urlUser = urlParams.get('user');
    let urlPass = urlParams.get('pass');
    if (urlPass?.endsWith('/')) {
        urlPass = urlPass.slice(0, -1);
    }
    const urlWatermark = urlParams.get('watermark');
    const urlRelacaoCarga = urlParams.get('relacao_carga');
    const urlIdAgendamento = urlParams.get('id_Agendamento');
    const urlProtocoloAno = urlParams.get('protocolo_ano');
    // Priority: URL > participant props > config > cookie > participant name
    return {
        login: urlUser || localParticipant?.user_SGI || config?.user_SGI || '',
        pass: urlPass || localParticipant?.pwd_SGI || config?.pwd_SGI || '',
        watermark: urlWatermark || localParticipant?.watermark_SGI || config?.watermark_SGI || getSgiCookie('abtra-fiscal') || localParticipant?.name || '',
        relacaoCarga: urlRelacaoCarga || localParticipant?.relacao_carga_SGI || config?.relacao_carga_SGI || '',
        idAgendamento: urlIdAgendamento || localParticipant?.id_Agendamento || config?.id_Agendamento || '',
        protocoloAno: urlProtocoloAno || localParticipant?.protocolo_ano_SGI || config?.protocolo_ano_SGI || ''
    };
}
